import React from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaInbox } from 'react-icons/fa';
import { adminColors } from '../../../theme/adminUi';

/**
 * Contenedor estético de vista previa tabular.
 */
export default function ReportPreviewPanel({
  title,
  countLabel,
  count = 0,
  loading = false,
  emptyMessage = 'Seleccioná un período y generá la vista previa.',
  emptyHint,
  columns = [],
  rows = [],
  getRowKey,
  renderCell,
  maxH = '62vh',
}) {
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const headBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const muted = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box
      borderWidth="1px"
      borderColor={border}
      borderRadius="lg"
      overflow="hidden"
      bg="white"
    >
      <Flex
        align="center"
        justify="space-between"
        gap={3}
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderColor={border}
        bg={headBg}
        flexWrap="wrap"
      >
        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
          {title}
        </Text>
        <Badge
          bg={count > 0 ? adminColors.primary : 'gray.200'}
          color={count > 0 ? 'white' : 'gray.600'}
          variant="solid"
          borderRadius="full"
          px={3}
          py={0.5}
          fontWeight="medium"
        >
          {countLabel || `${count} registros`}
        </Badge>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" py={16} gap={3}>
          <Spinner size="sm" color="gray.500" />
          <Text fontSize="sm" color={muted}>
            Generando vista previa…
          </Text>
        </Flex>
      ) : rows.length === 0 ? (
        <VStack spacing={2} py={14} px={6} color={muted}>
          <Box as={FaInbox} boxSize={8} opacity={0.45} />
          <Text fontSize="sm" fontWeight="medium" textAlign="center">
            {emptyMessage}
          </Text>
          {emptyHint ? (
            <Text fontSize="xs" textAlign="center" maxW="420px">
              {emptyHint}
            </Text>
          ) : null}
        </VStack>
      ) : (
        <TableContainer maxH={maxH} overflowY="auto" overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead bg={headBg} position="sticky" top={0} zIndex={1}>
              <Tr>
                {columns.map((col) => (
                  <Th
                    key={col.key}
                    isNumeric={col.isNumeric}
                    whiteSpace="nowrap"
                    color="gray.600"
                    fontSize="xs"
                    letterSpacing="0.02em"
                    borderColor={border}
                  >
                    {col.label}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, index) => (
                <Tr
                  key={getRowKey ? getRowKey(row, index) : index}
                  _hover={{ bg: rowHover }}
                >
                  {columns.map((col) => (
                    <Td
                      key={col.key}
                      isNumeric={col.isNumeric}
                      fontSize="sm"
                      borderColor={border}
                      py={2.5}
                      maxW={col.maxW}
                    >
                      {renderCell
                        ? renderCell(row, col)
                        : row[col.key] ?? '—'}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
